/**
 * The node's files: the project's assets attached to it (a character's portrait, a screen's
 * mock-up, a scene's script), each shown small with a button to see it and one to take it off;
 * "Attach" picks one of the project's files, "Upload" keeps a file of the computer and attaches
 * it. Each change is one `update_node { attachments }` (one undo). A file no longer in the
 * project is marked missing.
 */
import { For, Show, createMemo, type JSX } from "solid-js";
import type { Node } from "@protocol/Node";
import { useApp } from "../../app/context";
import { assetName } from "../../shared/assets";
import { toast } from "../../shared/toast";
import { openFile } from "../../state/assets";
import { AssetThumb } from "../../shared/AssetThumb";

/** The "Files" section of the node inspector (its title and fold are `NodePanel`'s). */
export function NodeFiles(props: { node: () => Node }): JSX.Element {
  const n = props.node;
  const { assets, presets, actions, access } = useApp();
  const canEdit = () => access.can("editor");
  const attached = () => n().attachments ?? [];
  /** The project's files not attached yet, to pick from. */
  const offered = createMemo(() => (assets.source("project")?.listing.files ?? []).filter((f) => !attached().includes(f.path)));
  /** Sets the node's list (one undo). */
  const set = (list: string[]) => void actions.updateNode(n().id, { attachments: list });
  let upload!: HTMLInputElement;
  return (
    <div class="node-files" id="node-files">
      <div class="stack">
        <For each={attached()}>
          {(path) => {
            const info = () => assets.info(path);
            return (
              <div class="row-inline file-row" data-attachment={path} classList={{ missing: !info() }}>
                <Show when={info()} fallback={<span class="aext">gone</span>}>
                  {(i) => <AssetThumb asset={i()} url={i().kind === "image" ? assets.url(path) : null} />}
                </Show>
                <button type="button" class="linklike file-name" title={info() ? "See it" : "This file is no longer in the project"} disabled={!info()} onClick={() => openFile(assets, presets, path)}>
                  {assetName(path)}
                </button>
                <Show when={canEdit()}>
                  <button type="button" class="mini" data-detach={path} title="Take it off this node (the file stays in the project)" onClick={() => set(attached().filter((p) => p !== path))}>
                    ✕
                  </button>
                </Show>
              </div>
            );
          }}
        </For>
        <Show when={canEdit()}>
          <div class="row-inline">
            <select
              class="select"
              id="node-attach"
              aria-label="Attach one of the project's files"
              disabled={!offered().length}
              onChange={(e) => {
                const path = e.currentTarget.value;
                e.currentTarget.value = "";
                if (path) set([...attached(), path]);
              }}
            >
              <option value="">{offered().length ? "Attach a file…" : "No other file in the project"}</option>
              <For each={offered()}>{(f) => <option value={f.path}>{f.path}</option>}</For>
            </select>
            <button type="button" class="mini" id="node-upload" onClick={() => upload.click()}>
              Upload
            </button>
            <input
              ref={upload}
              type="file"
              multiple
              hidden
              id="node-upload-file"
              onChange={(e) => {
                const list = [...(e.currentTarget.files ?? [])];
                e.currentTarget.value = "";
                if (!list.length) return;
                void assets.upload("project", list, "").then((kept) => {
                  if (!kept.length) return;
                  set([...attached(), ...kept.map((k) => k.path)]);
                  toast(kept.length === 1 ? `Attached ${kept[0]?.path}.` : `Attached ${kept.length} files.`);
                });
              }}
            />
          </div>
        </Show>
      </div>
    </div>
  );
}
